import { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { cn } from "../../lib/utils";

const TYPE_LABELS = { initiative: "Iniciativa", pilot: "Piloto", grant: "Ayuda" };

/**
 * Multi-select de entidades vinculadas a una Propuesta (Iniciativas/Portfolio,
 * Pilotos, Ayudas/Subvenciones — confirmado con el usuario 2026-09-08).
 * `entities` = [{ type, id, name }], `value`/`onChange` = LinkedEntity[].
 */
export function ProposalEntityPicker({ entities = [], value = [], onChange, testId }) {
  const [open, setOpen] = useState(false);
  const key = (e) => `${e.entity_type || e.type}:${e.entity_id || e.id}`;
  const selectedKeys = new Set(value.map((v) => `${v.entity_type}:${v.entity_id}`));

  const toggle = (e) => {
    const k = `${e.type}:${e.id}`;
    if (selectedKeys.has(k)) {
      onChange(value.filter((v) => `${v.entity_type}:${v.entity_id}` !== k));
    } else {
      onChange([...value, { entity_type: e.type, entity_id: e.id, entity_name: e.name }]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline" role="combobox" aria-expanded={open}
          className="w-full justify-between font-normal h-auto min-h-9 py-1.5"
          data-testid={testId}
        >
          <div className="flex flex-wrap gap-1 flex-1 text-left min-w-0 overflow-hidden">
            {value.length === 0 && <span className="text-muted-foreground">Vincular iniciativas, pilotos o ayudas...</span>}
            {value.map((v) => (
              <Badge key={key(v)} variant="secondary" className="text-xs gap-1 pr-1 max-w-full">
                <span className="text-[9px] uppercase opacity-60">{TYPE_LABELS[v.entity_type]}</span>
                <span className="truncate">{v.entity_name}</span>
                <X
                  className="w-3 h-3 cursor-pointer shrink-0"
                  onClick={(e) => { e.stopPropagation(); onChange(value.filter((x) => key(x) !== key(v))); }}
                />
              </Badge>
            ))}
          </div>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar iniciativa, piloto o ayuda..." />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup className="max-h-72 overflow-y-auto">
              {entities.map((e) => (
                <CommandItem key={`${e.type}:${e.id}`} value={`${TYPE_LABELS[e.type]} ${e.name}`} onSelect={() => toggle(e)}>
                  <Check className={cn("mr-2 h-4 w-4", selectedKeys.has(`${e.type}:${e.id}`) ? "opacity-100" : "opacity-0")} />
                  <span className="text-[9px] uppercase text-muted-foreground mr-2">{TYPE_LABELS[e.type]}</span>
                  <span className="truncate">{e.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
